import { useState, useEffect, useMemo } from 'react'
import { fetchGrowingMaterialPrices, fetchGrowingMaterials, upsertGrowingMaterialPrice, fetchPriceTiers } from '../supabase-client'
import { useDataCache } from '../contexts/DataCacheContext'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table'
import { Check, X } from 'lucide-react'

export default function GrowingMaterialPriceCatalogue() {
  const { invalidateCache } = useDataCache()
  const [prices, setPrices] = useState([])
  const [materials, setMaterials] = useState([])
  const [priceTiers, setPriceTiers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [editingCell, setEditingCell] = useState(null) // "materialId::tierId"
  const [editValue, setEditValue] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => { loadData() }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)
      const [pricesData, materialsData, tiersData] = await Promise.all([
        fetchGrowingMaterialPrices(),
        fetchGrowingMaterials(),
        fetchPriceTiers()
      ])
      setPrices(pricesData)
      setMaterials(materialsData.filter(m => m.active))
      setPriceTiers(tiersData)
    } catch (err) {
      console.error('Failed to load growing material price catalogue:', err)
      setError('Failed to load data. Please refresh.')
    } finally {
      setLoading(false)
    }
  }

  // Build price lookup: "materialId::tierId" → unit_price
  const priceMap = useMemo(() => {
    const map = new Map()
    prices.forEach(p => {
      map.set(`${p.growing_material_id}::${p.price_tier_id}`, parseFloat(p.unit_price))
    })
    return map
  }, [prices])

  const handleCellClick = (materialId, tierId) => {
    const key = `${materialId}::${tierId}`
    const currentPrice = priceMap.get(key)
    setEditingCell(key)
    setEditValue(currentPrice !== undefined ? String(currentPrice) : '')
  }

  const handleSave = async () => {
    if (!editingCell || saving) return

    const [materialId, tierId] = editingCell.split('::')
    const price = parseFloat(editValue)

    if (isNaN(price) || price < 0) {
      setEditingCell(null)
      return
    }

    try {
      setSaving(true)
      await upsertGrowingMaterialPrice(materialId, tierId, price)
      invalidateCache('growingMaterialPrices')

      setPrices(prev => {
        const existing = prev.find(p => p.growing_material_id === materialId && p.price_tier_id === tierId)
        if (existing) {
          return prev.map(p =>
            p.growing_material_id === materialId && p.price_tier_id === tierId
              ? { ...p, unit_price: price }
              : p
          )
        }
        return [...prev, { growing_material_id: materialId, price_tier_id: tierId, unit_price: price }]
      })
      setEditingCell(null)
    } catch (err) {
      console.error('Failed to save growing material price:', err)
      setError('Failed to save price.')
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    setEditingCell(null)
    setEditValue('')
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSave()
    if (e.key === 'Escape') handleCancel()
  }

  if (loading) return <div className="p-4 text-sm text-gray-500">Loading growing material prices...</div>

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle className="flex items-center gap-2">
            Growing Material Price Catalogue
          </CardTitle>
          <CardDescription>
            Set the unit price for each growing material at each pricing tier.
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent>
        {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
        {materials.length === 0 ? (
          <p className="text-sm text-gray-500">No active growing materials found. Add some in the Growing Materials tab first.</p>
        ) : (
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[220px]">Material</TableHead>
                  {priceTiers.map(tier => (
                    <TableHead key={tier.id} className="min-w-[140px]">
                      {tier.display_name || tier.name}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {materials.map(material => (
                  <TableRow key={material.id}>
                    <TableCell className="font-medium">
                      {material.name}
                      <span className="block text-xs text-gray-500">per {material.unit_label}</span>
                    </TableCell>
                    {priceTiers.map(tier => {
                      const key = `${material.id}::${tier.id}`
                      const unitPrice = priceMap.get(key)
                      const isEditing = editingCell === key

                      return (
                        <TableCell key={tier.id} className="p-2">
                          {isEditing ? (
                            <div className="flex items-center gap-1">
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onKeyDown={handleKeyDown}
                                className="w-20 h-8 text-sm"
                                autoFocus
                              />
                              <button onClick={handleSave} disabled={saving} className="p-1 text-green-600 hover:text-green-800">
                                <Check className="h-4 w-4" />
                              </button>
                              <button onClick={handleCancel} className="p-1 text-gray-400 hover:text-gray-600">
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <div
                              onClick={() => handleCellClick(material.id, tier.id)}
                              className="cursor-pointer hover:bg-gray-50 rounded px-2 py-1 min-h-[2.5rem] flex items-center"
                            >
                              {unitPrice !== undefined ? (
                                <span className="font-medium text-sm">£{unitPrice.toFixed(2)}/{material.unit_label}</span>
                              ) : (
                                <span className="text-gray-400 text-sm">Click to set</span>
                              )}
                            </div>
                          )}
                        </TableCell>
                      )
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
